import React, { useMemo } from 'react';
import {
  MediaDevice,
  EMicrophoneMenuItemKey,
  EVideoMenuItem,
  VideoFooterMenuOption,
} from 'shared/types/video.interface';
import { AUDIO_DATA } from 'shared/constants/zoom-constants';
import { getVideoFooterMenuOption } from 'utils/video-footer-utils';
import { ConferenceButton } from 'components/Button/ConferenceButton/ConferenceButton';

interface MicrophoneButtonProps {
  isStartedAudio: boolean;
  isMuted: boolean;
  isSupportPhone?: boolean;
  disabled?: boolean;
  audio?: string;
  phoneCountryList?: any[];
  onMicrophoneClick: () => void;
  onMicrophoneMenuClick: (key: string) => void;
  onPhoneCallClick?: (code: string, phoneNumber: string, name: string, option: any) => void;
  onPhoneCallCancel?: (code: string, phoneNumber: string, option: any) => Promise<any>;
  microphoneList?: MediaDevice[];
  speakerList?: MediaDevice[];
  activeMicrophone?: string;
  activeSpeaker?: string;
  phoneCallStatus?: { text: string; type: string };
}
const MicrophoneButtonMui = (props: MicrophoneButtonProps) => {
  const {
    isStartedAudio,
    isSupportPhone,
    isMuted,
    audio,
    microphoneList,
    speakerList,
    activeMicrophone,
    activeSpeaker,
    disabled,
    onMicrophoneClick,
    onMicrophoneMenuClick,
  } = props;

  const tooltipText = useMemo(() => {
    if (isStartedAudio && isMuted) return AUDIO_DATA.unmute;
    if (isStartedAudio && !isMuted) return AUDIO_DATA.mute;
    return AUDIO_DATA.join;
  }, [isMuted, isStartedAudio]);

  const microphoneIconType = useMemo(() => {
    if (isStartedAudio && isMuted) return 'icon-micro_off';
    if (isStartedAudio && !isMuted) return 'icon-micro_on';
    return 'icon-micro_off';
  }, [isMuted, isStartedAudio]);

  const menuItems = useMemo((): VideoFooterMenuOption[] => {
    const menuOptions: VideoFooterMenuOption[] = [];
    if (microphoneList?.length && audio !== 'phone') {
      menuOptions.push(
        getVideoFooterMenuOption(
          AUDIO_DATA.selectMicrophone,
          EMicrophoneMenuItemKey.microphone,
          microphoneList,
          activeMicrophone,
          EVideoMenuItem.group,
        ),
      );
    }
    if (speakerList?.length && audio !== 'phone') {
      menuOptions.push(
        getVideoFooterMenuOption(
          AUDIO_DATA.selectSpeaker,
          EMicrophoneMenuItemKey.speaker,
          speakerList,
          activeSpeaker,
          EVideoMenuItem.group,
        ),
      );
    }
    if (audio === 'phone') {
      menuOptions.push(getVideoFooterMenuOption(AUDIO_DATA.hangUp));
    } else {
      menuOptions.push(
        getVideoFooterMenuOption(AUDIO_DATA.leaveAudio, EMicrophoneMenuItemKey.leaveAudio),
      );
    }
    return menuOptions;
  }, [activeMicrophone, activeSpeaker, audio, microphoneList, speakerList]);

  const supportPhoneMenuItems: VideoFooterMenuOption[] = [
    getVideoFooterMenuOption(AUDIO_DATA.InviteByPhone, EMicrophoneMenuItemKey.phone),
  ];

  const onMenuItemClick = (key: string) => {
    onMicrophoneMenuClick(key);
  };

  const onPhoneMenuClick = () => {};

  return (
    <ConferenceButton
      fullButton={(!isMuted && isStartedAudio) || isSupportPhone}
      tooltipTitle={tooltipText}
      onButtonClick={onMicrophoneClick}
      isButtonDisabled={isStartedAudio ? disabled : undefined}
      buttonIconType={microphoneIconType}
      isMenuAvailable={(!isMuted && isStartedAudio) || isSupportPhone}
      menuOptions={isStartedAudio ? menuItems : supportPhoneMenuItems}
      onMenuItemClick={isStartedAudio ? onMenuItemClick : onPhoneMenuClick}
    />
  );
};

export default MicrophoneButtonMui;
